import type { Zone } from '@seek/audience-zones';
import type {
  TealiumExperimentProps,
  HubbleExperiments,
} from '@seek/candidate-feature-flag';
import { adapter } from '@seek/tealium-adapter';

import { SAVED_SEARCH_ROUTE_NAME } from '../../constants';

import createRecommendedJobsAnalytics, {
  type RecommendedJobsAnalytics,
} from './recommendedJobs';
import createSavedSearchesAnalytics, {
  type SavedSearchesAnalytics,
} from './savedSearches';

let tealium: ReturnType<typeof adapter>;

export const configureAdapter = (_adapter?: ReturnType<typeof adapter>) => {
  if (_adapter) {
    tealium = _adapter;
  } else {
    tealium = adapter({});
  }
};

export type TrackFn<N, D> = (
  eventType: EventType,
  eventName: N,
  eventData: D,
) => void;

export function trackFunction<N extends string, D extends Record<string, any>>(
  eventType: EventType,
  eventName: N,
  eventData: D,
) {
  // Fallback in case configureAdapter wasn't called
  if (!tealium) {
    configureAdapter();
  }
  tealium[eventType](eventName, eventData);
}

export enum LinkPosition {
  Body = 'body',
  ExpandedMenu = 'expanded menu',
  Footer = 'footer',
  Header = 'header',
  JobDetails = 'job details',
  Listing = 'listing',
  Below = 'below',
}

export type Analytics = RecommendedJobsAnalytics | SavedSearchesAnalytics;

export interface Experiments {
  experimentsEligible?: TealiumExperimentProps[];
  hubbleExperiments?: HubbleExperiments;
}

export interface DataLayer extends Experiments {
  linkName?: string;
  thirdPartyLink?: boolean;
  isThirdPartyLink?: boolean;
  siteCountry: string;
  brand: string;
  linkPosition?: string;
  currentPage: string;
  profileGuid: string;
  loginId: string;
  experimentId?: string;
  zone?: Zone;
}

export interface SetExperiments {
  setExperiments: (props: Experiments) => void;
}

export type EventType = 'link' | 'view';

export default (route: string, initialValues?: any): Analytics =>
  route === SAVED_SEARCH_ROUTE_NAME
    ? createSavedSearchesAnalytics(initialValues)
    : createRecommendedJobsAnalytics(initialValues);
